import omit from 'lodash/omit'
import map from 'lodash/map'
import get from 'lodash/get'
import gql from 'graphql-tag'
import { DataProxy } from '~/node_modules/apollo-cache/src/types/DataProxy'
import {
  GQLLocalCart,
  GQLLocalMutationAddToCartArgs,
  GQLLocalMutationChangeAmountArgs,
  GQLLocalMutationDeleteCartArgs,
  GQLLocalMutationDeleteCartDigitalArgs,
  GQLLocalMutationDeleteCartInsuranceArgs,
  GQLLocalMutationDeleteCartWarrantyArgs,
} from '~/graphql/__generated__/interfacesLocal'
import { accountMapper } from '~/graphql/gqlLocal/localAccount'
import { createWebApi } from '~/graphql/gqlLocal/localCsrfToken'
import { GetWidgetsData } from '~/utils/webApi.types'
import { ApolloCtx } from '~/plugins/apolloConfig'

export const GQL_LOCAL_CART_MARXY = gql`
  {
    cart @client {
      amount {
        articles
        pieces
      }
      price {
        total
      }
      articles {
        articleId
        count
        price {
          actual
        }
      }
      action {
        action
        amount
      }
      webSocket {
        articleId
      }
    }
  }
`

export const GQL_LOCAL_CART_AMOUNT = gql`
  {
    cart @client {
      amount {
        articles
        pieces
      }
    }
  }
`

export const GQL_LOCAL_GET_CART = gql`
  {
    cart @client {
      id
      cartId
      amount {
        articles
        pieces
      }
      price {
        total
      }
      articles {
        articleId
        title
        simpleTitle
        createTime
        articleType
        count
        mainMediaId
        count
        url
        hasGift
        variants {
          id
          title
          variantId
        }
        price {
          actual
          type
        }
        price {
          actual
          type
        }
        bundleId
        availability {
          inStock
          stock {
            translationKey
          }
        }
        bundles {
          digital {
            id
            title
            actualPrice
            active
            variantId
            itemId
          }
          warranty {
            id
            actualPrice
            type
            active
            variantId
            itemId
          }
          insurance {
            id
            actualPrice
            type
            active
            variantId
            itemId
          }
          returnGuarantee {
            id
            actualPrice
            type
            active
            variantId
            itemId
          }
        }
        marketplacePartner {
          id
          title
          url
        }
        params {
          css
          key
          title
          value
        }
      }
    }
  }
`

export const GQL_LOCAL_GET_CART_ARTICLE_IDS = gql`
  {
    cart @client {
      id
      articles {
        articleId
        count
        price {
          actual
        }
      }
    }
  }
`

export const GQL_LOCAL_MUTATION_ADD_TOC_CART = gql`
  mutation($addCart: CartAddInput!) {
    addToCart(addCart: $addCart) @client
  }
`

export const GQL_LOCAL_MUTATION_DELETE_CART = gql`
  mutation($cartId: ID!, $articleId: ID!) {
    deleteCart(cartId: $cartId, articleId: $articleId) @client
  }
`

export const GQL_LOCAL_MUTATION_DELETE_DIGITAL_CART = gql`
  mutation($id: ID!, $bundleId: ID!, $cartId: ID!) {
    deleteCartDigital(id: $id, bundleId: $bundleId, cartId: $cartId) @client
  }
`

export const GQL_LOCAL_MUTATION_DELETE_WARRANTY_CART = gql`
  mutation($cartId: ID!, $articleId: ID!, $variantId: ID!) {
    deleteCartWarranty(
      cartId: $cartId
      articleId: $articleId
      variantId: $variantId
    ) @client
  }
`

export const GQL_LOCAL_MUTATION_DELETE_INSURANCE_CART = gql`
  mutation($cartId: ID!, $articleId: ID!, $variantId: ID!) {
    deleteCartInsurance(
      cartId: $cartId
      articleId: $articleId
      variantId: $variantId
    ) @client
  }
`
export const GQL_LOCAL_MUTATION_RETURN_GUARANTEE_CART = gql`
  mutation($cartId: ID!, $articleId: ID!, $variantId: ID!) {
    deleteReturnGuarantee(
      cartId: $cartId
      articleId: $articleId
      variantId: $variantId
    ) @client
  }
`

export const GQL_LOCAL_MUTATION_AMOUNT_CART = gql`
  mutation($cartId: ID!, $articleId: ID!, $amount: Int!) {
    changeAmount(cartId: $cartId, articleId: $articleId, amount: $amount)
      @client
  }
`

export const MY_CART_ID = 'MY_CART'

// Initializing local state with an array stores incorrect value in the cache
// https://github.com/apollographql/apollo-client/issues/4498
const digitalMapper = (digitalItems: [], typename: string) => {
  const dataMap = map(digitalItems, (digital: any) => {
    return {
      ...digital,
      __typename: 'ArticlesBundlesDigitalItem',
      actualPrice: digital.actual_price,
      itemId: digital.id,
      id: `${digital.variantId}:${digital.id}`,
    }
  }).filter((digital: any) => digital.active)

  if (!dataMap.length) {
    return [
      {
        __typename: typename,
        active: null,
        actualPrice: null,
        actual_price: null,
        id: null,
        title: null,
        type: null,
        variantId: null,
        itemId: null,
      },
    ]
  }

  return dataMap
}

const bundlesMapper = (data: [], typename: string) => {
  const dataType = map(data, (digital: any) => {
    return {
      __typename: typename,
      actualPrice: digital.actual_price,
      ...digital,
      itemId: digital.id,
      id: `${digital.variantId}:${digital.id}`,
    }
  }).filter((digital: any) => digital.active)

  if (!dataType.length) {
    return [
      {
        __typename: typename,
        active: null,
        actualPrice: null,
        actual_price: null,
        decimals_round: null,
        id: null,
        type: null,
        vat_rate: null,
        variantId: null,
        itemId: null,
      },
    ]
  }

  return dataType
}

const bundlesMap = (bundles: any, articleId: string) => {
  return {
    __typename: 'ArticlesBundlesDigital',
    digital: digitalMapper(
      bundles?.digital,
      `ArticlesBundlesDigitalItem:${articleId}`
    ),
    id: articleId,
    insurance: bundlesMapper(
      bundles?.insurance,
      `ArticlesBundlesInsuranceItem:${articleId}`
    ),
    returnGuarantee: bundlesMapper(
      // eslint-disable-next-line camelcase
      bundles?.return_guarantee,
      `ArticlesBundlesReturnGuarantee:${articleId}`
    ),
    warranty: bundlesMapper(
      bundles?.warranty,
      `ArticlesBundlesWarrantyItem:${articleId}`
    ),
  }
}

const availabilityMap = (availability: any) => {
  return {
    __typename: 'ArticlePriceAvailability',
    translationKey: '',
    ...availability,
    stock: {
      __typename: 'ArticlePriceAvailabilityStock',
      translationKey: '',
      ...availability.stock,
    },
  }
}

const paramsMapper = (params: []) => {
  const dataType = map(params, (param: any) => {
    return {
      ...param,
      __typename: 'CartArticleParam',
    }
  })
  if (!dataType.length) {
    return [
      {
        __typename: 'CartArticleParam',
        css: null,
        key: null,
        title: null,
        value: null,
      },
    ]
  }

  return dataType
}

const variantsMapper = (params: []) => {
  const dataType = map(params, (param: any) => {
    return {
      ...param,
      id: param.variantId.toString(),
      variantId: param.variantId,
      __typename: 'CartArticleVariant',
    }
  })
  if (!dataType.length) {
    return [
      {
        __typename: 'CartArticleVariant',
        variantId: null,
        title: null,
        id: null,
      },
    ]
  }

  return dataType
}

// data is marked as type "any" object is large and needs to map only cart
const cartMapper = (data: GetWidgetsData | any): GQLLocalCart => {
  const articles =
    data.cart?.articles?.map((article: any) => {
      const marketplacePartnerData = {
        __typename: 'ArticleMarketplacePartner',
        id: null,
        title: null,
        url: null,
        ...(data.cart?.marketplacePartners[article.marketplacePartnerId] ?? {}),
      }

      return {
        ...omit(
          article,
          'bundles',
          'price',
          'product',
          'availability',
          'params'
        ),
        __typename: 'Articles',
        articleId: article.articleId.toString(),
        articleType: article.type,
        availability: availabilityMap(article.availability),
        bundleId: article.bundleId,
        bundles: bundlesMap(article.bundles, article.articleId),
        variants: variantsMapper(article.variants),
        mainMediaId: article.mainMediaId ?? null,
        marketplacePartner: marketplacePartnerData,
        params: paramsMapper(article.params ?? []),
        price: {
          ...article.price,
          __typename: 'ArticlePrice',
          actual: article.price.actual,
        },
        product: {
          ...(article.product ?? { id: null }),
          __typename: 'ArticlesProduct',
          urlKey: article.url.split('/')[1],
        },
      }
    }) ?? []

  return <GQLLocalCart>{
    __typename: 'Cart',
    action: {
      __typename: 'CartAction',
      ...(get(data, 'webSocket[0].cart') ?? { action: null, amount: null }),
    },
    amount: {
      __typename: 'CartAmount',
      ...(data?.cart?.amount ?? { articles: 0, pieces: 0 }),
    },
    articles,
    cartId: String(data.cart.cartId),
    id: MY_CART_ID,
    price: {
      __typename: 'CartPrice',
      ...(data.cart.price ?? { base: 0, total: 0 }),
    },
    webSocket: {
      __typename: 'CartAction',
      ...(data?.webSocket?.item ?? { articleId: null }),
    },
  }
}

export const mutationLocalCart = (ctx: ApolloCtx) => {
  return {
    addToCart: async (
      _: {},
      args: GQLLocalMutationAddToCartArgs,
      { cache }: { cache: DataProxy }
    ) => {
      const webApi = createWebApi(cache, ctx.app.$env.monolithUrl)
      const cartData = cache.readQuery({ query: GQL_LOCAL_GET_CART }) as {
        cart: GQLLocalCart
      }

      const cartId = cartData.cart?.cartId ?? null

      const putToCartInput = {
        ...args.addCart,
        bundles: JSON.stringify(args.addCart.bundles),
        id: cartId,
      }
      const response = await webApi.putToCart(putToCartInput)

      const cart = cartMapper(response)

      cache.writeData({
        data: {
          cart: {
            ...cart,
            cartId,
          },
        },
      })

      if (cart.amount.pieces === 0) {
        ctx.logger.error('Add cart prices is 0', putToCartInput, response)
      }
    },

    changeAmount: async (
      _: {},
      args: GQLLocalMutationChangeAmountArgs,
      { cache }: { cache: DataProxy }
    ) => {
      const { articleId, cartId, amount } = args
      const webApi = createWebApi(cache, ctx.app.$env.monolithUrl)
      const response = await webApi.changeAmountInCart({
        amount,
        cartId,
        id: articleId,
      })

      const cart = cartMapper(response)
      cache.writeData({
        data: {
          cart: { ...cart, cartId },
        },
      })
    },

    deleteCart: async (
      _: {},
      args: GQLLocalMutationDeleteCartArgs,
      { cache }: { cache: DataProxy }
    ) => {
      const { articleId, cartId } = args
      const webApi = createWebApi(cache, ctx.app.$env.monolithUrl)
      const response = await webApi.deleteFromCart(articleId, cartId)

      const cart = cartMapper(response)
      cache.writeData({
        data: {
          cart: { ...cart, cartId },
        },
      })
    },

    deleteCartDigital: async (
      _: {},
      args: GQLLocalMutationDeleteCartDigitalArgs,
      { cache }: { cache: DataProxy }
    ) => {
      const { bundleId, cartId, id } = args
      const webApi = createWebApi(cache, ctx.app.$env.monolithUrl)
      const response = await webApi.deleteDigitalContentFromCart({
        bundleId,
        cartId,
        id,
      })

      const cart = cartMapper(response)
      cache.writeData({
        data: { cart: { ...cart, cartId } },
      })
    },

    deleteCartInsurance: async (
      _: {},
      args: GQLLocalMutationDeleteCartInsuranceArgs,
      { cache }: { cache: DataProxy }
    ) => {
      const { cartId } = args
      const webApi = createWebApi(cache, ctx.app.$env.monolithUrl)
      const response = await webApi.deleteInsuranceFromCart(args)

      const cart = cartMapper(response)
      cache.writeData({
        data: { cart: { ...cart, cartId } },
      })
    },

    deleteCartWarranty: async (
      _: {},
      args: GQLLocalMutationDeleteCartWarrantyArgs,
      { cache }: { cache: DataProxy }
    ) => {
      const { cartId } = args

      const webApi = createWebApi(cache, ctx.app.$env.monolithUrl)
      const response = await webApi.deleteWarrantyFromCart(args)

      const cart = cartMapper(response)
      cache.writeData({
        data: { cart: { ...cart, cartId } },
      })
    },

    deleteReturnGuarantee: async (
      _: {},
      args: GQLLocalMutationDeleteCartWarrantyArgs,
      { cache }: { cache: DataProxy }
    ) => {
      const { cartId } = args
      const webApi = createWebApi(cache, ctx.app.$env.monolithUrl)
      const response = await webApi.deleteReturnGuaranteeFromCart(args)

      const cart = cartMapper(response)
      cache.writeData({
        data: { cart: { ...cart, cartId } },
      })
    },
  }
}

export const getLocalCart = (ctx: ApolloCtx) => {
  return {
    cart: async (
      _: {},
      args: { addCart: GQLLocalMutationAddToCartArgs },
      { cache }: { cache: DataProxy }
    ) => {
      const webApi = createWebApi(cache, ctx.app.$env.monolithUrl)

      const responseWidgetData = await webApi.getWidgetData()

      const cart = cartMapper(responseWidgetData)

      cache.writeData({
        data: {
          account: accountMapper(responseWidgetData),
          cart: { ...cart, cartId: cart?.cartId ?? null },
        },
      })
    },
  }
}
